import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, FileText, Pencil, Trash2, Copy, Check, X, Save, Eye, Languages, Search, Sparkles, Wand2 } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';
import GeneratePDFWizard from './GeneratePDFWizard';
import AutoOrganizeModal, { LearnedMappingsList } from '../../../components/admin/AutoOrganizeModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API_URL}/api/cms/document-templates`;

const TYPE_OPTIONS = [
  { value: 'loi', label: 'Letter of Intent', color: 'bg-[#023E8A] text-white' },
  { value: 'nda', label: 'NDA', color: 'bg-[#FF6B35] text-white' },
  { value: 'ncnda', label: 'NCNDA', color: 'bg-[#DC2626] text-white' },
  { value: 'confidentiality', label: 'Confidentiality', color: 'bg-[#00B4D8] text-white' },
  { value: 'cover_letter', label: 'Cover Letter', color: 'bg-[#6C757D] text-white' },
  { value: 'partnership', label: 'Partnership Agreement', color: 'bg-[#198754] text-white' },
  { value: 'spa', label: 'Sale & Purchase (SPA)', color: 'bg-[#8B5CF6] text-white' },
  { value: 'mou', label: 'Memorandum of Understanding', color: 'bg-[#0891B2] text-white' },
  { value: 'mou_form', label: 'MOU — Form Template', color: 'bg-[#0E7490] text-white' },
  { value: 'custom', label: 'Custom', color: 'bg-gray-800 text-white' },
];

const LANGUAGES = [
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'ar', label: 'العربية', flag: '🇦🇪' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
];

const typeBadgeClass = (t) => TYPE_OPTIONS.find((o) => o.value === t)?.color || 'bg-gray-200 text-gray-800';
const typeLabel = (t, customLabel) => {
  if (t === 'custom' && customLabel && customLabel.trim()) return customLabel.trim();
  return TYPE_OPTIONS.find((o) => o.value === t)?.label || t;
};

const DocumentTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [editor, setEditor] = useState({ open: false, mode: 'create', data: null });
  const [wizard, setWizard] = useState({ open: false, template: null });
  const { toast } = useToast();

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${ENDPOINT}/?active_only=false`);
      setTemplates(res.data || []);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (typeFilter && t.type !== typeFilter) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (t.code || '').toLowerCase().includes(s)
        || (t.name || '').toLowerCase().includes(s)
        || (t.description || '').toLowerCase().includes(s);
    });
  }, [templates, search, typeFilter]);

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${ENDPOINT}/${tpl.id}`);
      toast({ title: 'Deleted', description: tpl.name });
      fetchTemplates();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.response?.data?.detail || 'Delete failed' });
    }
  };

  const handleDuplicate = async (tpl) => {
    const code = window.prompt('New unique code for the duplicate:', `${tpl.code}_copy`);
    if (!code) return;
    try {
      await axios.post(`${ENDPOINT}/${tpl.id}/duplicate`, { code });
      toast({ title: 'Duplicated', description: code });
      fetchTemplates();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.response?.data?.detail || 'Duplicate failed' });
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="document-templates-page">
      <BackButton />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#023E8A]" /> Document Templates CMS
          </h1>
          <p className="text-sm text-[#6C757D] mt-1">
            Edit the default body of LOI, NDA, Confidentiality Agreements, Cover Letters and other templates.
            Placeholders like <code className="bg-gray-100 px-1 rounded">{'{supplier_name}'}</code> are auto-replaced at render time.
          </p>
        </div>
        <Button
          onClick={() => setEditor({ open: true, mode: 'create', data: null })}
          className="bg-[#FF6B35] hover:bg-[#E5562D] text-white"
          data-testid="doc-templates-new-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
            placeholder="Search by code, name or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="doc-templates-search"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm"
          data-testid="doc-templates-type-filter"
        >
          <option value="">All types</option>
          {TYPE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
        </select>
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-[#6C757D]">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-[#6C757D]">
              No templates match the current filter.
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((t) => (
                <div key={t.id} className="p-4 flex items-start justify-between hover:bg-gray-50" data-testid={`doc-template-row-${t.code}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={typeBadgeClass(t.type)}>{typeLabel(t.type, t.type_label_custom)}</Badge>
                      <span className="font-semibold text-[#2B2D42]">{t.name}</span>
                      <code className="text-xs text-[#6C757D] bg-gray-100 px-2 py-0.5 rounded">{t.code}</code>
                      {t.is_default && <Badge className="bg-green-100 text-green-800 border border-green-300">default</Badge>}
                      {!t.is_active && <Badge className="bg-gray-200 text-gray-700">inactive</Badge>}
                      <span className="text-xs text-gray-400">v{t.version}</span>
                    </div>
                    {t.description && <p className="text-sm text-[#6C757D] mt-1">{t.description}</p>}
                    {t.placeholders?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {t.placeholders.slice(0, 8).map((p) => (
                          <span key={p} className="text-[11px] bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded font-mono">
                            {`{${p}}`}
                          </span>
                        ))}
                        {t.placeholders.length > 8 && <span className="text-xs text-gray-400">+{t.placeholders.length - 8}</span>}
                      </div>
                    )}
                    <div className="text-[11px] text-gray-400 mt-1 flex gap-3">
                      <span>Languages: {LANGUAGES.filter((l) => (t[`body_${l.code}`] || '').trim()).map((l) => l.code.toUpperCase()).join(' · ') || '—'}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => setWizard({ open: true, template: t })}
                      className="bg-[#FF6B35] hover:bg-[#E5562D] text-white"
                      data-testid={`doc-template-generate-${t.code}`}
                      title="Generate PDF"
                    >
                      <Sparkles className="w-4 h-4 mr-1" /> Generate
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditor({ open: true, mode: 'edit', data: t })} data-testid={`doc-template-edit-${t.code}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDuplicate(t)} data-testid={`doc-template-duplicate-${t.code}`}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    {!t.is_default && (
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(t)} data-testid={`doc-template-delete-${t.code}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editor.open && (
        <TemplateEditor
          mode={editor.mode}
          template={editor.data}
          onClose={() => setEditor({ open: false, mode: 'create', data: null })}
          onSaved={() => { setEditor({ open: false, mode: 'create', data: null }); fetchTemplates(); }}
        />
      )}

      {wizard.open && wizard.template && (
        <GeneratePDFWizard
          template={wizard.template}
          onClose={() => setWizard({ open: false, template: null })}
        />
      )}
    </div>
  );
};


// --------- Editor modal ---------
const TemplateEditor = ({ mode, template, onClose, onSaved }) => {
  const { toast } = useToast();
  const isEdit = mode === 'edit';
  const [lang, setLang] = useState('pt');
  const [saving, setSaving] = useState(false);
  const [previewBody, setPreviewBody] = useState('');
  const [previewMissing, setPreviewMissing] = useState([]);
  const [form, setForm] = useState(() => ({
    code: template?.code || '',
    type: template?.type || 'custom',
    type_label_custom: template?.type_label_custom || '',
    name: template?.name || '',
    description: template?.description || '',
    is_default: !!template?.is_default,
    is_active: template?.is_active !== false,
    body_pt: template?.body_pt || '',
    body_en: template?.body_en || '',
    body_fr: template?.body_fr || '',
    body_ar: template?.body_ar || '',
    body_zh: template?.body_zh || '',
  }));
  const [contextSample, setContextSample] = useState(() => defaultSampleContext(template?.type || form.type));
  const [autoStructuring, setAutoStructuring] = useState(false);
  const [autoOrganizeResult, setAutoOrganizeResult] = useState(null);
  const [autoOrganizeOpen, setAutoOrganizeOpen] = useState(false);

  const handleAutoStructure = async () => {
    const current = form[`body_${lang}`] || '';
    if (!current.trim()) {
      toast({ title: 'Empty body', description: `Write something in the ${lang.toUpperCase()} body first.`, variant: 'destructive' });
      return;
    }
    setAutoStructuring(true);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.post(`${ENDPOINT}/auto-structure`, { body: current, language: lang }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = r.data || {};
      if (!data.normalized_body || data.normalized_body === current) {
        if ((data.warnings || []).length === 0) {
          toast({ title: 'Nothing to normalize', description: 'No blank form-fields detected in this body.' });
          return;
        }
      }
      setAutoOrganizeResult(data);
      setAutoOrganizeOpen(true);
    } catch (err) {
      toast({
        title: 'Auto-Organize failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setAutoStructuring(false);
    }
  };

  const applyAutoOrganize = (normalizedBody) => {
    setForm({ ...form, [`body_${lang}`]: normalizedBody });
  };

  // Extract live placeholders from current bodies
  const placeholders = useMemo(() => {
    const found = [];
    const re = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
    for (const key of ['body_pt', 'body_en', 'body_fr', 'body_ar', 'body_zh']) {
      const body = form[key] || '';
      let m;
      while ((m = re.exec(body)) !== null) {
        if (!found.includes(m[1])) found.push(m[1]);
      }
    }
    return found;
  }, [form.body_pt, form.body_en, form.body_fr, form.body_ar, form.body_zh]);

  // Local preview — render selected-language body with sample context, detect missing
  useEffect(() => {
    const body = form[`body_${lang}`] || '';
    let rendered = body;
    const missing = [];
    rendered = rendered.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (full, token) => {
      const v = contextSample[token];
      if (v === undefined || v === null || String(v).trim() === '') {
        if (!missing.includes(token)) missing.push(token);
        return full;
      }
      return String(v);
    });
    setPreviewBody(rendered);
    setPreviewMissing(missing);
  }, [form, contextSample, lang]);

  const onInsertPlaceholder = (token) => {
    const key = `body_${lang}`;
    setForm((p) => ({ ...p, [key]: `${p[key] || ''}{${token}}` }));
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast({ variant: 'destructive', title: 'Missing fields', description: 'Code and Name are required' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, code: form.code.trim(), placeholders };
      if (isEdit) {
        // Cannot PATCH code via router — keep it but backend ignores unknown keys; we'll only send known ones
        const { code, ...patchable } = payload;
        await axios.patch(`${ENDPOINT}/${template.id}`, patchable);
        toast({ title: 'Saved', description: `${form.name} updated — translations generate in background.` });
      } else {
        await axios.post(`${ENDPOINT}/`, payload);
        toast({ title: 'Created', description: `${form.name} saved — translations generate in background.` });
      }
      onSaved();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="doc-template-editor">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[94vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-[#2B2D42]">{isEdit ? 'Edit Template' : 'New Template'}</h3>
            <p className="text-xs text-[#6C757D]">Portuguese is the master language. Save to auto-translate EN/FR/AR/ZH in background.</p>
          </div>
          <button onClick={onClose} data-testid="doc-template-editor-close"><X className="w-5 h-5 text-gray-500" /></button>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-0">
            {/* Form column */}
            <div className="p-6 space-y-4 border-r">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#6C757D]">Code *</label>
                  <input
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                    placeholder="e.g. loi_supply_process"
                    value={form.code}
                    disabled={isEdit}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    data-testid="doc-template-code"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#6C757D]">Type *</label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    value={form.type}
                    onChange={(e) => { setForm({ ...form, type: e.target.value }); setContextSample(defaultSampleContext(e.target.value)); }}
                    data-testid="doc-template-type"
                  >
                    {TYPE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                  </select>
                </div>
              </div>
              {form.type === 'custom' && (
                <div className="-mt-1">
                  <label className="text-xs text-[#6C757D] flex items-center gap-1.5">
                    Custom type name
                    <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">free text</span>
                  </label>
                  <input
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="e.g. Service Agreement · Joint Venture · Term Sheet"
                    value={form.type_label_custom}
                    onChange={(e) => setForm({ ...form, type_label_custom: e.target.value })}
                    data-testid="doc-template-type-custom-label"
                  />
                  <p className="text-[11px] text-[#6C757D] mt-1">
                    This label appears on the template badge and replaces the generic "Custom" tag across the platform.
                  </p>
                </div>
              )}
              <div>
                <label className="text-xs text-[#6C757D]">Name *</label>
                <input
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="Letter of Intent — Supply Process"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="doc-template-name"
                />
              </div>
              <div>
                <label className="text-xs text-[#6C757D]">Description</label>
                <input
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> Default for this type
                </label>
              </div>

              {/* Language tabs */}
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <Languages className="w-4 h-4 text-[#023E8A]" />
                  <span className="text-sm font-medium text-[#2B2D42]">Body per language</span>
                  <span className="text-xs text-[#6C757D]">(edit PT and save — EN/FR/AR/ZH auto-translate)</span>
                </div>
                <div className="flex gap-1 flex-wrap mb-2 items-center">
                  {LANGUAGES.map((l) => {
                    const filled = (form[`body_${l.code}`] || '').trim().length > 0;
                    return (
                      <button
                        key={l.code}
                        type="button"
                        onClick={() => setLang(l.code)}
                        className={`px-3 py-1.5 text-xs rounded-lg border flex items-center gap-1 ${lang === l.code ? 'bg-[#023E8A] text-white border-[#023E8A]' : 'bg-white text-[#2B2D42] hover:bg-gray-50'}`}
                        data-testid={`doc-template-lang-${l.code}`}
                      >
                        <span>{l.flag}</span> {l.code.toUpperCase()} {filled && <Check className="w-3 h-3" />}
                      </button>
                    );
                  })}
                  <div className="flex-1" />
                  <button
                    type="button"
                    onClick={handleAutoStructure}
                    disabled={autoStructuring}
                    className="px-3 py-1.5 text-xs rounded-lg border border-[#FF6B35] bg-gradient-to-r from-[#FF6B35] to-[#F97316] text-white hover:opacity-90 flex items-center gap-1 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="doc-template-auto-organize-btn"
                    title="Auto-detect blank form fields (Label: _____) and convert to {placeholder} tokens"
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    {autoStructuring ? 'Organizing…' : 'Auto-Organize'}
                  </button>
                </div>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono leading-relaxed"
                  rows={16}
                  dir={lang === 'ar' ? 'rtl' : 'ltr'}
                  placeholder={lang === 'pt' ? 'Escreva aqui o corpo em Português (será auto-traduzido ao guardar)…' : `${lang.toUpperCase()} body (optional — will be auto-filled on save if left empty)`}
                  value={form[`body_${lang}`] || ''}
                  onChange={(e) => setForm({ ...form, [`body_${lang}`]: e.target.value })}
                  data-testid={`doc-template-body-${lang}`}
                />
                <p className="text-xs text-[#6C757D] mt-1">
                  Insert placeholders with curly braces: <code>{'{supplier_name}'}</code>, <code>{'{company_name}'}</code>, etc.
                  {' '}Or paste raw form text with blank lines (<code>Nome: _____</code>) and click <b className="text-[#FF6B35]">Auto-Organize</b> to convert to placeholders automatically.
                </p>
              </div>
            </div>

            {/* Preview column */}
            <div className="p-6 bg-gray-50 space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-[#2B2D42] flex items-center gap-2">
                  <Eye className="w-4 h-4 text-[#023E8A]" /> Live preview ({lang.toUpperCase()})
                </h4>
                <p className="text-xs text-[#6C757D]">Placeholders replaced with sample context. Missing values are kept as `{'{token}'}` and flagged below.</p>
              </div>

              <div className="bg-white rounded-lg border p-4 text-sm whitespace-pre-wrap min-h-[200px]" dir={lang === 'ar' ? 'rtl' : 'ltr'} data-testid="doc-template-preview">
                {previewBody || <span className="text-gray-400">Empty — start writing the body on the left.</span>}
              </div>
              {previewMissing.length > 0 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  Missing context values: {previewMissing.map((m) => `{${m}}`).join(', ')}
                </div>
              )}

              <div>
                <h4 className="text-sm font-semibold text-[#2B2D42] mb-2">Placeholders detected</h4>
                <div className="flex flex-wrap gap-1">
                  {placeholders.length === 0 && <span className="text-xs text-gray-400">None yet. Insert <code>{'{token}'}</code> in the body.</span>}
                  {placeholders.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => onInsertPlaceholder(p)}
                      className="text-[11px] bg-white border border-amber-200 text-amber-800 px-1.5 py-0.5 rounded font-mono hover:bg-amber-50"
                      title="Click to append to current body"
                    >
                      {`{${p}}`}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-[#2B2D42] mb-2">Sample context (preview only)</h4>
                <div className="space-y-1">
                  {Object.keys(contextSample).map((k) => (
                    <div key={k} className="flex items-center gap-2">
                      <code className="text-[11px] text-[#6C757D] w-32 truncate">{`{${k}}`}</code>
                      <input
                        className="flex-1 px-2 py-1 border rounded text-xs"
                        value={contextSample[k] ?? ''}
                        onChange={(e) => setContextSample({ ...contextSample, [k]: e.target.value })}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <LearnedMappingsList />
            </div>
          </div>
        </div>

        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-white">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#023E8A] hover:bg-[#0353A4] text-white"
            data-testid="doc-template-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create template')}
          </Button>
        </div>
      </div>

      <AutoOrganizeModal
        open={autoOrganizeOpen}
        onClose={() => setAutoOrganizeOpen(false)}
        initialResult={autoOrganizeResult}
        bodyOriginal={form[`body_${lang}`] || ''}
        language={lang}
        onApply={applyAutoOrganize}
      />
    </div>
  );
};


function defaultSampleContext(type) {
  const base = {
    supplier_name: 'TechSupply LLC',
    company_name: 'Grupo BMP',
    project_name: 'FTA 9% Rollout',
    validity_days: '30',
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 10),
    currency: 'AED',
  };
  if (type === 'nda') {
    return {
      party_a: 'Grupo BMP LLC',
      party_b: 'TechSupply LLC',
      effective_date: base.issue_date,
      duration_years: '3',
      governing_law: 'United Arab Emirates',
    };
  }
  if (type === 'ncnda') {
    return {
      effective_date: base.issue_date,
      contract_location: 'Dubai, UAE',
      party_a_name: 'Grupo Bruno Miguel Pegado, LLC',
      party_a_hq: 'Sharjah, UAE',
      party_a_registration: 'TRN 105342960900001',
      party_a_representative: 'Bruno Miguel Pegado',
      party_a_bank_details: 'Emirates NBD · IBAN AE00…',
      party_b_name: 'Introduced Party LLC',
      party_b_hq: 'Dubai, UAE',
      party_b_registration: '—',
      party_b_representative: 'Authorised Signatory',
      non_circumvention_years: '2',
      commission_pct: '5',
      contract_years: '3',
      notice_days: '60',
      governing_law: 'United Arab Emirates',
      arbitration_body: 'ICC',
      arbitration_seat: 'Paris',
      arbitration_language: 'English',
      arbitration_arbitrators: '1',
    };
  }
  if (type === 'confidentiality') {
    return { party_name: 'TechSupply LLC', project_name: base.project_name, issue_date: base.issue_date };
  }
  if (type === 'cover_letter') {
    return {
      recipient_name: 'TechSupply LLC',
      subject: 'RFQ Response Submission',
      issue_date: base.issue_date,
      company_name: base.company_name,
      sender_name: 'Bruno Pegado',
    };
  }
  if (type === 'partnership') {
    return {
      contract_date: base.issue_date,
      contract_location: 'Dubai, United Arab Emirates',
      financier_name: 'Grupo Bruno Miguel Pegado, LLC',
      financier_hq: 'Shams Media City, Sharjah, Tower B, Office 2501, UAE',
      financier_representative: 'Bruno Miguel Pegado',
      financier_role: 'Chief Executive Officer',
      manager_name: 'Grupo BMP Angola LDA',
      manager_hq: 'Luanda, Angola',
      manager_representative: '—',
      manager_role: 'Managing Director',
      profit_margin_min: '20',
      profit_margin_max: '35',
      profit_split_financier: '50',
      profit_split_manager: '50',
      contract_years: '5',
      notice_days: '90',
      records_retention_years: '5',
      arbitration_body: 'International Chamber of Commerce (ICC)',
      arbitration_seat: 'Paris, France',
      arbitration_language: 'English',
      arbitration_arbitrators: '1',
    };
  }
  if (type === 'spa') {
    return {
      contract_number: 'SPA-2026-0001',
      contract_date: base.issue_date,
      contract_location: 'Dubai, United Arab Emirates',
      seller_name: 'Grupo Bruno Miguel Pegado, LLC',
      seller_hq: 'Shams Media City, Sharjah, UAE',
      seller_registration: 'TRN 105342960900001',
      seller_representative: 'Bruno Miguel Pegado',
      seller_role: 'Chief Executive Officer',
      buyer_name: 'TechSupply LLC',
      buyer_hq: 'Dubai, UAE',
      buyer_registration: 'TRN 100123456789012',
      buyer_representative: 'Buyer Representative',
      buyer_role: 'Procurement Director',
      product_description: 'Frozen Boneless Beef — mixed cuts as per annex',
      quantity: '20,000',
      quantity_unit: 'kg',
      product_origin: 'Brazil',
      product_specifications: 'Halal certified; frozen below -18°C; vacuum packed.',
      unit_price: '6.30',
      currency: 'USD',
      total_value: '126,000',
      incoterm: 'CIF',
      incoterm_version: 'Incoterms 2020',
      delivery_location: 'Jebel Ali Port, Dubai',
      delivery_deadline: base.due_date,
      transport_mode: 'Sea freight — 40ft reefer container',
      inspection_body: 'SGS or equivalent independent body',
      inspection_cost_party: 'Buyer',
      payment_terms: 'LC, followed by Full Payment upon Shipping Documents and Physical Inspection or SGS or Similar.',
      seller_bank: 'Emirates NBD — Dubai',
      late_delivery_cap: '10',
      late_payment_cap: '10',
      confidentiality_years: '3',
      governing_law: 'United Arab Emirates',
      arbitration_body: 'International Chamber of Commerce (ICC)',
      arbitration_seat: 'Paris, France',
      arbitration_language: 'English',
      arbitration_arbitrators: '1',
    };
  }
  return base;
}

export default DocumentTemplates;
